import PropTypes from 'prop-types';
import { Box, TextField } from '@material-ui/core';
import placeholder_illustration from 'assets/img/placeholder_illustration.png';
import GeneralGrid from 'common/components/layout/grid/GeneralGrid';
import LoadingProgress from 'common/components/progress/loading';
import Table from 'common/components/table/table-material';
import React, { useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import _ from 'lodash';
import * as XLSX from 'xlsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBook } from '@fortawesome/free-solid-svg-icons';
import {
  parseISO,
  isAfter,
  isBefore,
  isValid,
  addDays,
  subDays,
  subMonths,
  format,
} from 'date-fns';

const formatDateForInput = (d) => format(d, 'yyyy-MM-dd');
const today = new Date();
const DEFAULT_START_DATE = formatDateForInput(subMonths(today, 6));
const DEFAULT_END_DATE = formatDateForInput(today);

// 1. Adicione esta função auxiliar para converter a data
function parseCustomDateString(dateString) {
  if (!dateString || typeof dateString !== 'string') {
    return null;
  }
  // Converte 'DD-MM-YYYY HH:mm' para 'YYYY-MM-DDTHH:mm:00'
  const parts = dateString.split(' ');
  if (parts.length < 2) return null;

  const dateParts = parts[0].split('-');
  if (dateParts.length < 3) return null;

  const [day, month, year] = dateParts;
  const timePart = parts[1];

  // Cria uma string no formato ISO que parseISO consegue entender
  const isoString = `${year}-${month}-${day}T${timePart}:00`;
  return parseISO(isoString);
}

// Função para sanitizar valores e evitar erro 501 no Excel (interpretação de fórmulas)
function sanitizeExcelValue(value) {
  if (value === null || value === undefined) {
    return '';
  }
  const str = String(value);
  // Se começar com =, +, -, @ ou tab, adiciona apóstrofo para evitar interpretação como fórmula
  if (/^[=+\-@\t]/.test(str)) {
    return "'" + str;
  }
  return str;
}

export default function ProductOrders(props) {
  const { id } = props.auth.user.establishments[0].stations[0];
  const { router } = props;
  const { list, loading, deliveryStatusesList } = props.productOrders;

  const [columns, setColumns] = useState([]);
  const [csvData, setCSVData] = useState(null);
  const [activityFilter, setActivityFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [cpfFilter, setCpfFilter] = useState('');
  const [startDate, setStartDate] = useState(DEFAULT_START_DATE);
  const [endDate, setEndDate] = useState(DEFAULT_END_DATE);
  const [statusFilter, setStatusFilter] = useState('');

  function renderFullPrice(item) {
    return <div>{`R$ ${parseFloat(item).toFixed(2).toString().replace('.', ',')}`}</div>;
  }

  function handleOpenDetailModal(_, rowData) {
    router.push(`/owner/sales/${rowData.id}`);
  }

  useEffect(() => {
    props.getDeliveryStatuses();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      props.getOrdersWithId(id, startDate, endDate);
    }, 500);
    return () => clearTimeout(timer);
  }, [startDate, endDate]);

  useEffect(() => {
    const deliveryStatusLookup = {};
    for (let i = 0; i < deliveryStatusesList.length; i += 1) {
      deliveryStatusLookup[`${deliveryStatusesList[i].id}`] = deliveryStatusesList[i].status;
    }

    if (deliveryStatusesList.length > 0) {
      setColumns([
        {
          field: 'purchase_id',
          title: 'ID',
          editable: 'never',
        },
        {
          field: 'gateway_charge.external_id',
          title: 'ID Pagar.me',
          editable: 'never',
          render: (rowData) => rowData.gateway_charge?.external_id,
        },
        {
          field: 'action.name',
          title: 'Atividade',
          render: (rowData) => rowData.action?.name,
        },
        {
          field: 'createdAt',
          title: 'Data',
          editable: 'never',
        },
        {
          field: 'user.name',
          title: 'Nome',
          editable: 'never',
          render: (rowData) => rowData.user?.name,
        },
        {
          field: 'user.cpf',
          title: 'CPF',
          editable: 'never',
          render: (rowData) => rowData.user?.cpf,
        },
        {
          title: 'Valor Final',
          field: 'final_value',
          render: (rowData) => {
            return renderFullPrice(rowData.final_value);
          },
          editable: 'never',
        },
        {
          title: 'Valor pago da carteira',
          field: 'wallet_value',
          render: (rowData) => {
            return renderFullPrice(rowData.wallet_value);
          },
          editable: 'never',
          export: true,
        },
        {
          title: 'Valor líquido da carteira',
          render: (rowData) => {
            const futureTransaction = rowData.future_transactions?.find(
              (item) => !item.gateway_or_wallet
            );
            if (futureTransaction) {
              return renderFullPrice(futureTransaction.value);
            } else if (rowData.status === 'Pagamento recusado') {
              return 'R$ 0,00';
            }
            return '—';
          },
          editable: 'never',
        },
        {
          title: 'Valor pago no cartão',
          field: 'total_credit_card_value',
          render: (rowData) => {
            const samePurchases = props.productOrders.list.filter(
              (item) => item.purchase_id === rowData.purchase_id
            );
            let totalWalletValue = 0;
            samePurchases.forEach((item) => {
              totalWalletValue += parseFloat(item.wallet_value);
            });
            if (totalWalletValue > 0) {
              return renderFullPrice(
                parseFloat(rowData.total_credit_card_value) *
                  (parseFloat(rowData.wallet_value) / totalWalletValue)
              );
            }
            return renderFullPrice(parseFloat(rowData.total_credit_card_value));
          },
          editable: 'never',
        },
        {
          title: 'Valor líquido do cartão',
          render: (rowData) => {
            const futureTransaction = rowData.future_transactions.find(
              (item) => item.gateway_or_wallet
            );
            if (futureTransaction) {
              return renderFullPrice(futureTransaction.value);
            } else if (rowData.status === 'Pagamento recusado') {
              return 'R$ 0,00';
            }
            return 'R$ 0,00';
          },
          editable: 'never',
        },
        {
          title: 'Alíquota (%)',
          render: (rowData) => {
            // Valor bruto = final_value
            // Valor líquido = soma dos valores líquidos da carteira e do cartão
            const futureTransactionCarteira = rowData.future_transactions?.find(
              (item) => !item.gateway_or_wallet
            );
            const futureTransactionCartao = rowData.future_transactions?.find(
              (item) => item.gateway_or_wallet
            );
            const valorLiquidoCarteira = futureTransactionCarteira
              ? parseFloat(futureTransactionCarteira.value)
              : 0;
            const valorLiquidoCartao = futureTransactionCartao
              ? parseFloat(futureTransactionCartao.value)
              : 0;
            const valorLiquido = valorLiquidoCarteira + valorLiquidoCartao;
            const valorBruto = parseFloat(rowData.final_value);

            if (valorBruto > 0 && valorLiquido > 0) {
              const aliquota = (1 - valorLiquido / valorBruto) * 100;
              return `${aliquota.toFixed(2)}%`;
            }
            return '-';
          },
          editable: 'never',
        },
        {
          title: 'Status',
          field: 'status',
          editable: 'never',
        },
        {
          field: 'address',
          title: 'Endereço',
          editable: 'never',
          render: (rowData) => (rowData.address ? rowData.address : ' '),
        },
        {
          field: 'reference',
          title: 'Complemento',
          editable: 'never',
          render: (rowData) => (rowData.reference ? rowData.reference : ' '),
        },
        {
          field: 'delivery_location',
          title: 'Local de Entrega',
          editable: 'never',
          render: (rowData) => rowData.delivery_location?.name || ' ',
        },
      ]);
    }
  }, [deliveryStatusesList.length]);

  useEffect(() => {
    const samePurchaseArray = [];
    const formattedCSVData = [
      [
        'ID',
        'ID Pagar.me',
        'Atividade',
        'Data',
        'Nome',
        'CPF',
        'Produtos',
        'Valor final',
        'Valor pago da carteira',
        'Valor líquido da carteira',
        'Valor pago no cartão',
        'Valor líquido do cartão/PIX',
        'Status',
        'Método de pagamento',
      ],
    ];
    for (let i = 0; i < props.productOrders.list.length; i += 1) {
      const item = props.productOrders.list[i];
      /* const index = samePurchaseArray.findIndex(secondItem => {
        return secondItem.purchase_id === item.purchase_id;
      }); */
      const futureTransaction = item.future_transactions.find(
        (secondItem) => !secondItem.gateway_or_wallet
      );
      const futureTransactionCreditCard = item.future_transactions.find(
        (secondItem) => secondItem.gateway_or_wallet
      );
      const samePurchases = props.productOrders.list.filter(
        (secondItem) => secondItem.purchase_id === item.purchase_id
      );
      let totalWalletValue = 0;
      samePurchases.forEach((secondItem) => {
        totalWalletValue += parseFloat(secondItem.wallet_value);
      });
      const notRepeatedProducts = [];
      item.single_product_orders.forEach((secondItem) => {
        const indexFound = notRepeatedProducts.findIndex(
          (thirdItem) => thirdItem.id === secondItem.id
        );
        if (indexFound >= 0) {
          notRepeatedProducts[indexFound] = {
            ...notRepeatedProducts[indexFound],
            quantity: notRepeatedProducts[indexFound].quantity + 1,
          };
        } else {
          notRepeatedProducts.push({
            name: secondItem.product.name,
            quantity: 1,
          });
        }
      });
      samePurchaseArray.push({
        ...item,
      });
      formattedCSVData.push([
        item.purchase_id,
        sanitizeExcelValue(item.gateway_charge ? item.gateway_charge.external_id : ''),
        sanitizeExcelValue(item.action ? item.action.name : ''),
        sanitizeExcelValue(item.createdAt),
        sanitizeExcelValue(item.user?.name),
        sanitizeExcelValue(item.user?.cpf),
        sanitizeExcelValue(
          notRepeatedProducts
            .map((secondItem) => `${secondItem.name} x${secondItem.quantity}`)
            .join('; ')
        ),
        item.final_value.toString().replace('.', ','),
        item.wallet_value.toString().replace('.', ','),
        futureTransaction
          ? futureTransaction.value.toString().replace('.', ',')
          : item.status === 'Pagamento recusado' ||
            item.wallet_value === '0.00' ||
            (item.gateway_charge?.payment_method === 'pix' && item.status === 'Expirado')
          ? '0,00'
          : '',
        totalWalletValue > 0
          ? _.round(
              parseFloat(item.total_credit_card_value) *
                (parseFloat(item.wallet_value) / totalWalletValue),
              2
            )
              .toString()
              .replace('.', ',')
          : item.total_credit_card_value.toString().replace('.', ','),
        futureTransactionCreditCard
          ? futureTransactionCreditCard.value.toString().replace('.', ',')
          : item.status === 'Pagamento recusado' || item.total_credit_card_value === '0.00'
          ? '0,00'
          : '',
        sanitizeExcelValue(item.status),
        item.gateway_charge
          ? item.gateway_charge.payment_method === 'pix'
            ? 'PIX'
            : 'Cartão de crédito'
          : parseFloat(item.total_credit_card_value) > 0
          ? 'Cartão de crédito'
          : 'Somente carteira',
      ]);
      /* samePurchaseArray[index] = {
          ...samePurchaseArray[index],
          wallet_value: parseFloat(
            (
              parseFloat(samePurchaseArray[index].wallet_value) +
              parseFloat(item.wallet_value)
            ).toFixed(2)
          ),
          final_value: parseFloat(
            (
              parseFloat(samePurchaseArray[index].final_value) +
              parseFloat(item.final_value)
            ).toFixed(2)
          ),
        };
        formattedCSVData.push([
          samePurchaseArray[index].purchase_id,
          samePurchaseArray[index].gateway_charge
            ? samePurchaseArray[index].gateway_charge.external_id
            : '',
          samePurchaseArray[index].action
            ? samePurchaseArray[index].action.name
            : '',
          samePurchaseArray[index].createdAt,
          samePurchaseArray[index].user.name,
          samePurchaseArray[index].user.cpf,
          parseFloat(
            (
              parseFloat(samePurchaseArray[index].wallet_value) +
              parseFloat(item.wallet_value)
            ).toFixed(2)
          ),
          futureTransaction
            ? futureTransaction.value
            : item.status === 'Pagamento recusado'
            ? 0
            : 'Erro',
          samePurchaseArray[index].total_credit_card_value,
          futureTransactionCreditCard
            ? futureTransactionCreditCard.value
            : item.status === 'Pagamento recusado'
            ? 0
            : 'Erro',
          parseFloat(
            (
              parseFloat(samePurchaseArray[index].final_value) +
              parseFloat(item.final_value)
            ).toFixed(2)
          ),
          item.status,
        ]); */
    }
    setCSVData(formattedCSVData);
  }, [props.productOrders.list]);
  function renderEmptyScreen() {
    return (
      <section id="statement-buy-points" style={{ height: '100vh' }}>
        <GeneralGrid container spacing={3} alignItems="center">
          <GeneralGrid item xs={12} md={6}>
            <GeneralGrid container spacing={3}>
              <GeneralGrid item xs={12}>
                <h2 className="signup-text">Você ainda não tem nenhum pedido</h2>
                <p>Os pedidos serão feitos pelos participantes e aparecerão aqui.</p>
              </GeneralGrid>
            </GeneralGrid>
          </GeneralGrid>
          <GeneralGrid item xs={12} md={6}>
            <img alt="" src={placeholder_illustration} className="img-fluid" />
          </GeneralGrid>
        </GeneralGrid>
      </section>
    );
  }
  // Filtra a lista pelo nome da atividade, nome do usuário, CPF, datas e status
  const filteredList = list.filter((item) => {
    const activityMatch = activityFilter
      ? item.action?.name?.toLowerCase().includes(activityFilter.toLowerCase())
      : true;
    const nameMatch = nameFilter
      ? item.user?.name?.toLowerCase().includes(nameFilter.toLowerCase())
      : true;
    const cpfMatch = cpfFilter
      ? item.user?.cpf?.replace(/\D/g, '').includes(cpfFilter.replace(/\D/g, ''))
      : true;

    // 2. Use a nova função para converter a data do item
    const date = parseCustomDateString(item.createdAt);
    const start = startDate ? parseISO(startDate) : null;
    const end = endDate ? parseISO(endDate) : null;

    const startMatch = start
      ? date &&
        isValid(date) &&
        (isAfter(date, subDays(start, 1)) || date.getTime() === start.getTime())
      : true;
    const endMatch = end
      ? date &&
        isValid(date) &&
        (isBefore(date, addDays(end, 1)) || date.getTime() === end.getTime())
      : true;

    const statusMatch = statusFilter ? item.status === statusFilter : true;
    return activityMatch && nameMatch && cpfMatch && startMatch && endMatch && statusMatch;
  });

  // Função para gerar o Excel apenas com os dados filtrados
  function gerarExcelComFiltro() {
    const formattedCSVData = [
      [
        'ID',
        'ID Pagar.me',
        'Atividade',
        'Data',
        'Nome',
        'CPF',
        'Produtos',
        'Valor final',
        'Valor pago da carteira',
        'Valor líquido da carteira',
        'Valor pago no cartão',
        'Valor líquido do cartão/PIX',
        'Alíquota (%)',
        'Status',
        'Método de pagamento',
        'Endereço',
        'Complemento',
        'Entrega',
      ],
    ];
    filteredList.forEach((item) => {
      const futureTransaction = item.future_transactions.find(
        (secondItem) => !secondItem.gateway_or_wallet
      );
      const futureTransactionCreditCard = item.future_transactions.find(
        (secondItem) => secondItem.gateway_or_wallet
      );
      const samePurchases = list.filter(
        (secondItem) => secondItem.purchase_id === item.purchase_id
      );
      let totalWalletValue = 0;
      samePurchases.forEach((secondItem) => {
        totalWalletValue += parseFloat(secondItem.wallet_value);
      });
      const notRepeatedProducts = [];
      item.single_product_orders.forEach((secondItem) => {
        const indexFound = notRepeatedProducts.findIndex(
          (thirdItem) => thirdItem.id === secondItem.id
        );
        if (indexFound >= 0) {
          notRepeatedProducts[indexFound] = {
            ...notRepeatedProducts[indexFound],
            quantity: notRepeatedProducts[indexFound].quantity + 1,
          };
        } else {
          notRepeatedProducts.push({
            name: secondItem.product.name,
            quantity: 1,
          });
        }
      });

      // Cálculo da alíquota
      const valorBruto = parseFloat(item.final_value);
      const valorLiquidoCarteira = futureTransaction ? parseFloat(futureTransaction.value) : 0;
      const valorLiquidoCartao = futureTransactionCreditCard
        ? parseFloat(futureTransactionCreditCard.value)
        : 0;
      const valorLiquido = valorLiquidoCarteira + valorLiquidoCartao;
      let aliquota = '-';
      if (valorBruto > 0 && valorLiquido > 0) {
        aliquota = ((1 - valorLiquido / valorBruto) * 100).toFixed(2).replace('.', ',') + '%';
      }

      formattedCSVData.push([
        item.purchase_id,
        sanitizeExcelValue(item.gateway_charge ? item.gateway_charge.external_id : ''),
        sanitizeExcelValue(item.action ? item.action.name : ''),
        sanitizeExcelValue(item.createdAt),
        sanitizeExcelValue(item.user?.name),
        sanitizeExcelValue(item.user?.cpf),
        sanitizeExcelValue(
          notRepeatedProducts
            .map((secondItem) => `${secondItem.name} x${secondItem.quantity}`)
            .join('; ')
        ),
        item.final_value.toString().replace('.', ','),
        item.wallet_value.toString().replace('.', ','),
        futureTransaction
          ? futureTransaction.value.toString().replace('.', ',')
          : item.status === 'Pagamento recusado' ||
            item.wallet_value === '0.00' ||
            (item.gateway_charge?.payment_method === 'pix' && item.status === 'Expirado')
          ? '0,00'
          : '',
        totalWalletValue > 0
          ? _.round(
              parseFloat(item.total_credit_card_value) *
                (parseFloat(item.wallet_value) / totalWalletValue),
              2
            )
              .toString()
              .replace('.', ',')
          : item.total_credit_card_value.toString().replace('.', ','),
        futureTransactionCreditCard
          ? futureTransactionCreditCard.value.toString().replace('.', ',')
          : item.status === 'Pagamento recusado' || item.total_credit_card_value === '0.00'
          ? '0,00'
          : '',
        aliquota,
        sanitizeExcelValue(item.status),
        item.gateway_charge
          ? item.gateway_charge.payment_method === 'pix'
            ? 'PIX'
            : 'Cartão de crédito'
          : parseFloat(item.total_credit_card_value) > 0
          ? 'Cartão de crédito'
          : 'Somente carteira',
        sanitizeExcelValue(item.address || ''),
        sanitizeExcelValue(item.reference || ''),
        sanitizeExcelValue(item.delivery_location?.name || ''),
      ]);
    });
    const ws = XLSX.utils.aoa_to_sheet(formattedCSVData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Vendas');
    XLSX.writeFile(wb, 'Vendas.xlsx');
  }

  if (loading || !columns.find((item) => item.field === 'gateway_charge.external_id')) {
    return (
      <Box display="flex" height="100vh" justifyContent="center" mt={7}>
        <LoadingProgress />
      </Box>
    );
  }
  if (list.length < 1) {
    return renderEmptyScreen();
  }

  return (
    <section id="statement-buy-points">
      <GeneralGrid container spacing={3} mt={2}>
        <GeneralGrid item xs={12} style={{ marginTop: 20, width: isMobile ? '89vw' : undefined }}>
          {/* Botão de exportar Excel alinhado à direita e usando os filtros */}
          {filteredList.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
              <button
                onClick={gerarExcelComFiltro}
                aria-label="Gerar Excel"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                }}>
                <FontAwesomeIcon
                  icon={faBook}
                  style={{
                    color: 'green',
                    fontSize: '25px',
                    verticalAlign: 'middle',
                  }}
                />
              </button>
            </div>
          )}
          {/* Filtros lado a lado */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
            <TextField
              label="Filtrar por atividade"
              variant="outlined"
              size="small"
              value={activityFilter}
              onChange={(e) => setActivityFilter(e.target.value)}
            />
            <TextField
              label="Filtrar por nome"
              variant="outlined"
              size="small"
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
            />
            <TextField
              label="Filtrar por CPF"
              variant="outlined"
              size="small"
              value={cpfFilter}
              onChange={(e) => setCpfFilter(e.target.value)}
            />
            <TextField
              label="Data início"
              type="date"
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            <TextField
              label="Data fim"
              type="date"
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
            <TextField
              label="Status"
              variant="outlined"
              size="small"
              select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              SelectProps={{ native: true }}
              style={{ minWidth: 150 }}
              InputLabelProps={{ shrink: true }} // <-- Adicione esta linha
            >
              <option value="">Todos</option>
              {/* Gera as opções de status dinamicamente */}
              {[...new Set(list.map((item) => item.status))].filter(Boolean).map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </TextField>
          </div>
          <Table
            title="Vendas"
            columns={columns}
            dataType={'sales'}
            rows={filteredList}
            onRowClick={handleOpenDetailModal}
            mobileTable={isMobile}
            hasBorder={!isMobile}
            loading={loading ? 'pending' : false}
          />
        </GeneralGrid>
      </GeneralGrid>
    </section>
  );
}

ProductOrders.propTypes = {
  auth: PropTypes.shape({
    user: PropTypes.shape({
      establishments: PropTypes.any,
    }),
  }),
  getDeliveryStatuses: PropTypes.func,
  getOrdersWithId: PropTypes.func,
  productOrders: PropTypes.shape({
    deliveryStatusesList: PropTypes.shape({
      length: PropTypes.number,
    }),
    list: PropTypes.shape({
      filter: PropTypes.func,
      length: PropTypes.number,
    }),
    loading: PropTypes.any,
  }),
  router: PropTypes.shape({
    push: PropTypes.func,
  }),
};
